import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { JobRunData, JobRunApiResponse } from './jobRun.types';

// Standardized GET function for job runs list
export async function fetchJobRuns({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  jobName,
  jobDetailId,
  startDate,
  endDate,
  startDateValue,
  endDateValue,
  status,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  jobName?: string;
  jobDetailId?: number;
  startDate?: string;
  endDate?: string;
  startDateValue?: string;
  endDateValue?: string;
  status?: string;
  searched?: boolean;
}): Promise<JobRunData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobName) params.jobName = jobName;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (startDateValue) params.startDateValue = startDateValue;
  if (endDateValue) params.endDateValue = endDateValue;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB.JOB_RUN_DETAIL_LIST);
  const response: JobRunApiResponse[] = await api.get<JobRunApiResponse[]>(endpoint, params);
  
  return response.map((jobRun: JobRunApiResponse) => ({
    id: jobRun.id,
    jobName: jobRun.jobName,
    startTime: jobRun.startTime,
    endTime: jobRun.endTime,
    fileName: jobRun.fileName,
    successCount: jobRun.successCount,
    failureCount: jobRun.failureCount,
    clientId: jobRun.clientId,
    jobDetailId: jobRun.jobDetailId,
    createdBy: jobRun.createdBy,
    creationDate: jobRun.creationDate,
    lastUpdateDate: jobRun.lastUpdateDate,
    lastUpdatedBy: jobRun.lastUpdatedBy,
    startDate: jobRun.startDate,
    endDate: jobRun.endDate,
  }));
}

// GET job runs count
export async function fetchJobRunsCount({
  clientCode,
  clientId,
  jobName,
  jobDetailId,
  startDate,
  endDate,
  startDateValue,
  endDateValue,
  status,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  jobName?: string;
  jobDetailId?: number;
  startDate?: string;
  endDate?: string;
  startDateValue?: string;
  endDateValue?: string;
  status?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (jobName) params.jobName = jobName;
  if (jobDetailId) params.jobDetailId = jobDetailId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (startDateValue) params.startDateValue = startDateValue;
  if (endDateValue) params.endDateValue = endDateValue;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOB.JOB_RUN_DETAIL_LIST}count`);
  const response = await api.get<any>(endpoint, params);
  
  if (typeof response === 'number') {
    return response;
  } else if (response && typeof response.count === 'number') {
    return response.count;
  }
  
  logger.warn('Invalid count response:', response);
  return 0;
}

// GET job run by ID
export async function fetchJobRunById({
  clientCode,
  jobRunId,
}: {
  clientCode: string;
  jobRunId: number;
}): Promise<JobRunData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOBS.RUN}/${jobRunId}`);
  const response: JobRunApiResponse = await api.get<JobRunApiResponse>(endpoint);
  
  return {
    id: response.id,
    jobName: response.jobName,
    startTime: response.startTime,
    endTime: response.endTime,
    fileName: response.fileName,
    successCount: response.successCount,
    failureCount: response.failureCount,
    clientId: response.clientId,
    jobDetailId: response.jobDetailId,
    createdBy: response.createdBy,
    creationDate: response.creationDate,
    lastUpdateDate: response.lastUpdateDate,
    lastUpdatedBy: response.lastUpdatedBy,
    startDate: response.startDate,
    endDate: response.endDate,
  };
}

// CREATE job run
export async function createJobRun({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<JobRunData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.RUN);
  const response: JobRunApiResponse = await api.post<JobRunApiResponse>(endpoint, payload);
  
  return {
    id: response.id,
    jobName: response.jobName,
    startTime: response.startTime,
    endTime: response.endTime,
    fileName: response.fileName,
    successCount: response.successCount,
    failureCount: response.failureCount,
    clientId: response.clientId,
    jobDetailId: response.jobDetailId,
    createdBy: response.createdBy,
    creationDate: response.creationDate,
    lastUpdateDate: response.lastUpdateDate,
    lastUpdatedBy: response.lastUpdatedBy,
    startDate: response.startDate,
    endDate: response.endDate,
  };
}

// UPDATE job run
export async function updateJobRun({
  clientCode,
  jobRunId,
  payload,
}: {
  clientCode: string;
  jobRunId: number;
  payload: any;
}): Promise<JobRunData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOBS.RUN}/${jobRunId}`);
  const response: JobRunApiResponse = await api.put<JobRunApiResponse>(endpoint, payload);
  
  return {
    id: response.id,
    jobName: response.jobName,
    startTime: response.startTime,
    endTime: response.endTime,
    fileName: response.fileName,
    successCount: response.successCount,
    failureCount: response.failureCount,
    clientId: response.clientId,
    jobDetailId: response.jobDetailId,
    createdBy: response.createdBy,
    creationDate: response.creationDate,
    lastUpdateDate: response.lastUpdateDate,
    lastUpdatedBy: response.lastUpdatedBy,
    startDate: response.startDate,
    endDate: response.endDate,
  };
}

// DELETE job run
export async function deleteJobRun({
  clientCode,
  jobRunId,
}: {
  clientCode: string;
  jobRunId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOBS.RUN}/${jobRunId}`);
  return api.delete(endpoint);
}
